import React, { useState } from 'react';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  Label,
  FormFeedback,
  Spinner,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Select from 'react-select';
import api from '../../helpers/api';
import { countryOptions } from '../../constants/utils';
import logo from '../../assets/images/white logo png.png';
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

const Register = (props) => {
    const { t } = props;
    document.title = t('shared.register', "Register") + " | ArcMedix";

    const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneCode: '',
      contactNumber: '',
      orgName: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Please enter your first name'),
      lastName: Yup.string().required('Please enter your last name'),
      email: Yup.string().email('Please enter a valid email').required('Please enter your email'),
      phoneCode: Yup.string().required('Please select a country code'),
      contactNumber: Yup.string().matches(/^\d{7,15}$/, 'Must be 7-15 digits').required('Please enter your phone number'),
      orgName: Yup.string().required('Please enter your organization name'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Please enter a password'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords do not match')
        .required('Please confirm your password'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post('auth/org-admin-signup', {
          name: `${values.firstName.trim()} ${values.lastName.trim()}`,
          email: values.email,
          phoneCode: values.phoneCode,
          contactNumber: values.contactNumber,
          orgName: values.orgName,
          password: values.password,
        });

        if (response.status === 200 || response.status === 201) {
          const { id, token, username, organization, profileUrl, role, permissions, orgApprovalStatus } = response.data.response;

          // Check org approval status before storing credentials
          if (orgApprovalStatus === 'REJECTED') {
            setError('Your organization registration was not approved. Please contact support.');
            return;
          }

          // Store token and user info for auto-login
          localStorage.setItem(
            'authUser',
            JSON.stringify({
              id,
              username,
              organization,
              profileUrl,
              role,
              permissions: permissions || [],
              orgApprovalStatus,
            })
          );

          // Redirect based on org approval status
          if (orgApprovalStatus === 'PENDING') {
            navigate('/pending-approval');
          } else {
            navigate(role === 'ROLE_REQUESTOR' ? '/service-requests' : '/reactive');
          }
        }
      } catch (err) {
        setError(
          err.response?.data?.message || err.response?.data?.error || 'Registration failed'
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="authentication-bg min-vh-100">
        <div className="bg-overlay"></div>
        <Container>
          <div className="d-flex flex-column min-vh-100 px-3">
            <Row className="justify-content-center my-auto">
              <Col md={8} lg={6} xl={5}>
                <div className="text-center">
                  <Link to="/">
                    <img src={logo} alt="" height="90" width="200" />
                  </Link>
                </div>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-center">
                      <h5 className="text-primary">Create Your Account</h5>
                      <p className="text-muted">
                        Register your organization on Arcmedix
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      {error && (
                        <Alert color="danger">
                          <div className="d-flex align-items-center">
                            <i className="bx bx-error-circle me-2"></i>
                            <div>{error}</div>
                          </div>
                        </Alert>
                      )}

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <Row>
                          <Col xs={6}>
                            <div className="mb-3">
                              <Label className="form-label">First Name <span className="text-danger">*</span></Label>
                              <Input
                                name="firstName"
                                type="text"
                                placeholder="First name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.firstName}
                                invalid={validation.touched.firstName && !!validation.errors.firstName}
                              />
                              {validation.touched.firstName && validation.errors.firstName && (
                                <FormFeedback>{validation.errors.firstName}</FormFeedback>
                              )}
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="mb-3">
                              <Label className="form-label">Last Name <span className="text-danger">*</span></Label>
                              <Input
                                name="lastName"
                                type="text"
                                placeholder="Last name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.lastName}
                                invalid={validation.touched.lastName && !!validation.errors.lastName}
                              />
                              {validation.touched.lastName && validation.errors.lastName && (
                                <FormFeedback>{validation.errors.lastName}</FormFeedback>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <div className="mb-3">
                          <Label className="form-label">Email <span className="text-danger">*</span></Label>
                          <Input
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={validation.touched.email && !!validation.errors.email}
                          />
                          {validation.touched.email && validation.errors.email && (
                            <FormFeedback>{validation.errors.email}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Phone Number <span className="text-danger">*</span></Label>
                          <div className="d-flex gap-2">
                            <Select
                              options={countryOptions}
                              value={countryOptions.find((c) =>
                                validation.values.phoneCode === c.dialCode.replace('+', '')
                              ) || null}
                              onChange={(selected) => {
                                validation.setFieldValue('phoneCode', selected?.dialCode.replace('+', '') || '');
                              }}
                              onBlur={() => validation.setFieldTouched('phoneCode', true)}
                              filterOption={(option, inputValue) => {
                                if (!inputValue) return true;
                                const searchText = inputValue.toLowerCase();
                                const labelText = option.data.labelText?.toLowerCase() || '';
                                const name = option.data.name?.toLowerCase() || '';
                                const dialCode = option.data.dialCode?.toLowerCase() || '';
                                return labelText.includes(searchText) ||
                                  name.includes(searchText) ||
                                  dialCode.includes(searchText);
                              }}
                              className="flex-grow-0"
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  width: '150px',
                                  borderColor: validation.touched.phoneCode && validation.errors.phoneCode
                                    ? '#f46a6a' : base.borderColor,
                                }),
                              }}
                              isClearable
                              placeholder="Code"
                            />
                            <Input
                              name="contactNumber"
                              type="tel"
                              className="flex-grow-1"
                              placeholder="Phone number"
                              value={validation.values.contactNumber}
                              onChange={(e) => validation.setFieldValue('contactNumber', e.target.value.replace(/\D/g, ''))}
                              onBlur={validation.handleBlur}
                              invalid={validation.touched.contactNumber && !!validation.errors.contactNumber}
                            />
                          </div>
                          {validation.touched.phoneCode && validation.errors.phoneCode && (
                            <small className="text-danger">{validation.errors.phoneCode}</small>
                          )}
                          {validation.touched.contactNumber && validation.errors.contactNumber && (
                            <small className="text-danger">{validation.errors.contactNumber}</small>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Organization Name <span className="text-danger">*</span></Label>
                          <Input
                            name="orgName"
                            type="text"
                            placeholder="Enter organization name"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.orgName}
                            invalid={validation.touched.orgName && !!validation.errors.orgName}
                          />
                          {validation.touched.orgName && validation.errors.orgName && (
                            <FormFeedback>{validation.errors.orgName}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Password <span className="text-danger">*</span></Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder="Enter password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                          />
                          {validation.touched.password && validation.errors.password && (
                            <FormFeedback>{validation.errors.password}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm Password <span className="text-danger">*</span></Label>
                          <Input
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm your password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword}
                            invalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                          />
                          {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                            <FormFeedback>{validation.errors.confirmPassword}</FormFeedback>
                          )}
                        </div>

                        <div className="mt-4 text-center">
                          <button
                            type="submit"
                            className="btn btn-primary w-sm waves-effect waves-light"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Creating account...
                              </>
                            ) : (
                              'Register'
                            )}
                          </button>
                        </div>
                      </Form>

                      <div className="mt-4 text-center">
                        <p className="mb-0">
                          Already have an account?{' '}
                          <Link to="/login" className="text-primary fw-semibold">
                            Login
                          </Link>
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted p-4">
                  <p className="text-white-50">
                      {t('shared.copyright', 'Copyright ©')} {new Date().getFullYear()} {t('shared.arcmedix_all_rights_reserved', 'Arcmedix. All rights reserved')}
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Register);

Register.propTypes = {
  t: PropTypes.func,
};
